import React, { FC, Fragment, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import { Avatar, Button, Checkbox, TableCell, TableRow } from '@mui/material';
import { Notification } from 'models/notification';
import NotificationIcon from 'components/navigation/app-bar/notifications/notifications-icons.component';
import { useTranslation } from 'react-i18next';
import NotificationSubject from './notification-subject.component';
import NotificationOriginator from './notification-originator.component';
import NotificationNotesDialog from './notification-notes-dialog.component';
import { useMutation } from '@apollo/client/react';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import {
    toggleNotificationReadTemplate,
    setNotificationDoneTemplate,
    updateNotificationResolutionTemplate,
} from 'apollo/templates/notification/mutation.graphql';
import { useNavigate } from 'react-router-dom';
import { getNotificationSubjectUrl } from 'utils/notification-navigation';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

type NotificationItemProps = {
    notification: Notification;
    onDoneChanged?: () => void;
};
const NotificationListItem: FC<NotificationItemProps> = ({ notification, onDoneChanged }: NotificationItemProps) => {
    const { t: translate } = useTranslation(['common', 'notifications']);
    const [isRead, setIsRead] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [notesComment, setNotesComment] = useState('');
    const [notesDialogOpen, setNotesDialogOpen] = useState(false);
    const navigate = useNavigate();
    const { showBoundary } = useDenthubErrorBoundary();
    const currentUser = currentUserVar();
    const isLabTechnician = permissions.isLabTechnician(currentUser.type);

    const [toggleNotificationRead] = useMutation(toggleNotificationReadTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [setNotificationDone] = useMutation(setNotificationDoneTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [updateNotificationResolution] = useMutation(updateNotificationResolutionTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setIsRead(notification.isRead);
        setIsDone(notification.isDone || false);
        setNotesComment(notification.resolution?.comment || '');
    }, [notification]);

    const toggleNotification = () => {
        toggleNotificationRead({
            variables: { id: notification._id, isRead: !isRead },
        });
        setIsRead(!isRead);
    };

    const toggleDone = () => {
        setNotificationDone({
            variables: { id: notification._id, isDone: !isDone },
            onCompleted: () => {
                // Let the list re-apply its done filter once the change is saved
                onDoneChanged?.();
            },
        });

        setIsDone(!isDone);
    };

    const handleSaveNotes = (comment: string) => {
        updateNotificationResolution({
            variables: { id: notification._id, comment },
        });
        setNotesComment(comment);
        setNotesDialogOpen(false);
    };

    const subjectUrl = getNotificationSubjectUrl(notification.type, notification.messageData.subjectId);

    const handleRowClick = () => {
        if (subjectUrl) {
            navigate(subjectUrl);
        }
    };

    const truncatedNotes = notesComment.length > 40 ? `${notesComment.substring(0, 40)}...` : notesComment;

    return (
        <Fragment>
            <TableRow
                hover
                onClick={handleRowClick}
                sx={{
                    bgcolor: notification.isRead ? 'inherit' : 'calm.light',
                    opacity: notification.isRead ? 0.8 : 1,
                    cursor: subjectUrl ? 'pointer' : 'default',
                }}
            >
                <TableCell>
                    <Avatar sx={{ bgcolor: notification.isRead ? 'grey.500' : 'secondary.main' }}>
                        <NotificationIcon type={notification.type} />
                    </Avatar>
                </TableCell>
                {isLabTechnician ? (
                    <TableCell>
                        <Checkbox
                            color="success"
                            checked={isDone}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => toggleDone()}
                        />
                    </TableCell>
                ) : undefined}
                <TableCell sx={{ minWidth: 150 }}>{formatDate(new Date(notification.createdAt), 'datetime')}</TableCell>
                <TableCell sx={{ width: 225 }}>
                    {translate(`notifications:NOTIFICATION_${notification.type}`)}
                </TableCell>
                <TableCell sx={{ width: 400 }}>
                    <NotificationSubject notification={notification} />
                </TableCell>
                <TableCell sx={{ width: 240 }}>
                    <NotificationOriginator originator={notification.originator} />
                </TableCell>
                {isLabTechnician ? (
                    <TableCell sx={{ width: 200 }}>{notification.responsibleLabUser?.fullName}</TableCell>
                ) : undefined}
                {isLabTechnician ? <TableCell sx={{ width: 200 }}>{truncatedNotes}</TableCell> : undefined}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {isLabTechnician ? (
                        <Button
                            sx={{ whiteSpace: 'nowrap' }}
                            onClick={(event) => {
                                event.stopPropagation();
                                setNotesDialogOpen(true);
                            }}
                        >
                            {translate('notifications:EDIT_NOTES')}
                        </Button>
                    ) : undefined}
                    <Button
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={(event) => {
                            event.stopPropagation();
                            toggleNotification();
                        }}
                    >
                        {isRead ? (
                            <span>{translate('notifications:MARK_AS_UNREAD')}</span>
                        ) : (
                            <span>{translate('notifications:MARK_AS_READ')}</span>
                        )}
                    </Button>
                </TableCell>
            </TableRow>
            <NotificationNotesDialog
                open={notesDialogOpen}
                initialComment={notesComment}
                onClose={() => setNotesDialogOpen(false)}
                onSave={handleSaveNotes}
            />
        </Fragment>
    );
};

export default NotificationListItem;
